/**
 * FP3-076A — Create Provider Modal
 *
 * Used by Org Admins to roster a new provider BEFORE sending an
 * invite. Provider/staff type (SLP, OT, Special Education Teacher,
 * Behavior Specialist, etc.) is captured here but kept independent of
 * the app role — creating a provider never grants admin privileges.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

// FP3-076A-1 — Auto-derive role_type + assistant_type from
// the chosen provider type. Mirrors `InviteUserModal.jsx` exactly
// so the rostering flow and the invite flow stay consistent.
//
// Why this matters: `provider_role_type` is internally just a
// non-assistant vs assistant classifier. Surfacing "Primary" or
// "Assistant" as a USER-facing dropdown made org admins think
// "Primary" was a global student-ownership identity, when it is
// not — student-level Primary Provider lives on
// `students.primary_provider_id` and is assigned per-student from
// Caseload. To avoid that confusion entirely, we no longer expose
// the binary as a "Role" picker.
const PROVIDER_TYPE_META = {
  "":                              { role_type: "primary",   assistant_type: "" },
  "SLP":                           { role_type: "primary",   assistant_type: "" },
  "OT":                            { role_type: "primary",   assistant_type: "" },
  "PT":                            { role_type: "primary",   assistant_type: "" },
  "Special Education Teacher":     { role_type: "primary",   assistant_type: "" },
  "School Psychologist":           { role_type: "primary",   assistant_type: "" },
  "Behavior Specialist":           { role_type: "primary",   assistant_type: "" },
  "BCBA":                          { role_type: "primary",   assistant_type: "" },
  "Counselor":                     { role_type: "primary",   assistant_type: "" },
  "Reading Specialist":            { role_type: "primary",   assistant_type: "" },
  "Interventionist":               { role_type: "primary",   assistant_type: "" },
  "Other":                         { role_type: "primary",   assistant_type: "" },
  // Assistant credentials require a supervising provider.
  "SLPA":                          { role_type: "assistant", assistant_type: "SLPA" },
  "COTA":                          { role_type: "assistant", assistant_type: "COTA" },
  "PTA":                           { role_type: "assistant", assistant_type: "PTA" },
};

const PROVIDER_TYPES = Object.keys(PROVIDER_TYPE_META);

// FP3-090 — Mirror the backend's discipline-family gate so the
// multi-select only offers compatible supervisors (SLPA→SLP,
// COTA→OT, PTA→PT). Unknown/blank roles stay eligible — the backend
// is permissive when it can't classify.
export const EXPECTED_FAMILY = { SLPA: "speech", COTA: "ot", PTA: "pt" };
export function familyOf(role) {
  const r = (role || "").toString().toLowerCase();
  if (!r) return "";
  if (r.includes("speech") || r.includes("slp")) return "speech";
  if (r.includes("occupational") || r.includes("cota") || /\bot\b/.test(r)) return "ot";
  if (r.includes("physical") || r.includes("pta") || /\bpt\b/.test(r)) return "pt";
  if (r.includes("bcba") || r.includes("behavior")) return "behavior";
  if (r.includes("special education teacher")) return "education";
  return "";
}

export default function CreateProviderModal({
  open,
  onClose,
  onCreated,
  schools = [],
  providersForSupervisor = [],
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [providerType, setProviderType] = useState("SLP");
  // FP3-090 — multi-select: an assistant may work under one or MORE
  // supervising providers at the organization level.
  const [supervisorIds, setSupervisorIds] = useState([]);
  const [schoolIds, setSchoolIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Derive role_type + assistant_type from provider_type — never
  // ask the user for them as a separate decision.
  const meta = PROVIDER_TYPE_META[providerType] || PROVIDER_TYPE_META[""];
  const isAssistantType = meta.role_type === "assistant";

  // Reset on each open.
  useEffect(() => {
    if (!open) return;
    setFirstName("");
    setLastName("");
    setEmail("");
    setProviderType("SLP");
    setSupervisorIds([]);
    setSchoolIds(schools.length === 1 ? [schools[0].id] : []);
    setSubmitting(false);
  }, [open, schools]);

  // FP3-090 — switching away from an assistant credential clears any
  // previously-picked supervisors so a stale selection never submits.
  useEffect(() => {
    if (!isAssistantType) setSupervisorIds([]);
  }, [isAssistantType]);

  // Eligible supervisor candidates: non-assistant, non-inactive, and
  // discipline-compatible (or unclassifiable) for the chosen type.
  const supervisorOptions = useMemo(() => {
    if (!isAssistantType) return [];
    const expected = EXPECTED_FAMILY[meta.assistant_type] || "";
    return providersForSupervisor
      .filter((p) => (p.account_status || "") !== "inactive")
      .filter((p) => {
        if (!expected) return true;
        const fam = familyOf(p.role);
        return !fam || fam === expected;
      });
  }, [isAssistantType, meta.assistant_type, providersForSupervisor]);

  const canSubmit = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    // Assistant credential → at least one supervisor required.
    if (isAssistantType && supervisorIds.length === 0) return false;
    return true;
  }, [firstName, lastName, isAssistantType, supervisorIds]);

  const toggleSchool = (id) => {
    setSchoolIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const toggleSupervisor = (id) => {
    setSupervisorIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        // Map the "_blank" sentinel back to an empty string — Select
        // disallows empty-string SelectItem values, so we use the
        // sentinel internally and unwrap it at submit-time.
        provider_type: providerType === "_blank" ? "" : providerType,
        // FP3-076A-1 — Auto-derived from provider type, never user-set.
        provider_role_type: meta.role_type,
        school_ids: schoolIds,
      };
      const trimmedEmail = email.trim();
      if (trimmedEmail) body.email = trimmedEmail;
      if (isAssistantType) {
        body.assistant_type = meta.assistant_type;
        // FP3-090 — plural canonical list (no priority order).
        body.supervising_provider_ids = supervisorIds;
      }
      const r = await api.createRosterProvider(body);
      toast.success(
        trimmedEmail
          ? `Provider rostered — invite them when ready`
          : `Provider rostered — add an email to enable invite`,
      );
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create provider";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="create-provider-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-[#1F2937]">
            Roster a new provider
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#6B7280] mt-1">
            Add a provider to the roster now. Send the activation invite
            later when you&apos;re ready. Provider/staff type is separate
            from app role — rostering a provider never grants admin
            access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cp-first" className="text-[11px] text-[#6B7280]">
                First name
              </Label>
              <Input
                id="cp-first"
                data-testid="create-provider-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-9 text-[13px] mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="cp-last" className="text-[11px] text-[#6B7280]">
                Last name
              </Label>
              <Input
                id="cp-last"
                data-testid="create-provider-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-9 text-[13px] mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="cp-email" className="text-[11px] text-[#6B7280]">
              Email <span className="text-[#9CA3AF]">· optional, required before invite</span>
            </Label>
            <Input
              id="cp-email"
              type="email"
              data-testid="create-provider-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="provider@school.example"
              className="h-9 text-[13px] mt-1"
            />
            <p className="text-[10px] text-[#6B7280] mt-1">
              Leave blank to roster now; add the email and click
              &quot;Invite provider&quot; later.
            </p>
          </div>

          <div>
            <Label className="text-[11px] text-[#6B7280]">Provider type</Label>
            <Select
              value={providerType}
              onValueChange={(v) => setProviderType(v)}
            >
              <SelectTrigger
                data-testid="create-provider-type"
                className="h-9 text-[13px] mt-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDER_TYPES.map((t) => (
                  <SelectItem
                    key={t || "blank"}
                    value={t || "_blank"}
                    data-testid={`create-provider-type-${(t || "blank").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t || "— Not specified —"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p
              className="text-[10px] text-[#6B7280] mt-1 leading-snug"
              data-testid="create-provider-type-helper"
            >
              Provider type captures what kind of practitioner this person
              is. Whether they are the Primary Provider for a student is set
              later on that student&apos;s team.
            </p>
          </div>

          {isAssistantType && (
            <div
              className="border border-[#E5E3DB] bg-[#FAFAF7] rounded-lg p-2 space-y-2"
              data-testid="create-provider-assistant-block"
            >
              <p className="text-[10px] text-[#6B7280] leading-snug">
                {providerType} requires at least one supervising provider on
                record. Select every provider who will supervise this
                assistant — the per-student supervisor is chosen later on
                each student&apos;s team.
              </p>
              <div>
                <Label className="text-[11px] text-[#6B7280]">
                  Supervising providers
                </Label>
                <div
                  className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto border border-[#ECEAE4] rounded-lg p-2 bg-white"
                  data-testid="create-provider-supervisors"
                >
                  {supervisorOptions.length === 0 && (
                    <p
                      className="text-[11px] text-[#A8453A]"
                      data-testid="create-provider-supervisors-empty"
                    >
                      No eligible supervising providers found for {providerType}.
                    </p>
                  )}
                  {supervisorOptions.map((p) => {
                    const on = supervisorIds.includes(p.id);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`create-provider-supervisor-${p.id}`}
                        aria-pressed={on}
                        onClick={() => toggleSupervisor(p.id)}
                        className={`text-[11px] px-2 py-1 rounded-full border transition-colors inline-flex items-center gap-1 ${
                          on
                            ? "bg-[#E8F0EB] border-[#1E3F33] text-[#1E3F33] font-medium"
                            : "bg-white border-[#E5E3DB] text-[#6B7280] hover:bg-[#FAFAF7]"
                        }`}
                      >
                        {p.name}
                        {p.role ? <span className="text-[9.5px] opacity-70">· {p.role}</span> : null}
                        {on && <span aria-hidden="true">×</span>}
                      </button>
                    );
                  })}
                </div>
                {supervisorIds.length > 0 && (
                  <p
                    className="text-[10px] text-[#6B7280] mt-1"
                    data-testid="create-provider-supervisors-count"
                  >
                    {supervisorIds.length} supervisor{supervisorIds.length === 1 ? "" : "s"} selected — tap a selected name to remove.
                  </p>
                )}
              </div>
            </div>
          )}

          {schools.length > 0 && (
            <div>
              <Label className="text-[11px] text-[#6B7280]">
                Schools / campuses
              </Label>
              <div className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto border border-[#ECEAE4] rounded-lg p-2 bg-[#FAFAF7]">
                {schools.map((s) => {
                  const on = schoolIds.includes(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`create-provider-school-${s.id}`}
                      onClick={() => toggleSchool(s.id)}
                      className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                        on
                          ? "bg-[#E8F0EB] border-[#1E3F33] text-[#1E3F33] font-medium"
                          : "bg-white border-[#E5E3DB] text-[#6B7280] hover:bg-[#FAFAF7]"
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="create-provider-cancel"
            onClick={() => onClose?.()}
            disabled={submitting}
            className="h-8 text-[12px]"
          >
            Cancel
          </Button>
          <Button
            data-testid="create-provider-submit"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white h-8 text-[12px]"
          >
            {submitting ? "Creating…" : "Create provider"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
